import React from "react";
import Reveal from "./Reveal";
import { useT } from "@/hooks/useT";
import {
  CalendarDays,
  Sparkles,
  Eye,
  BedDouble,
  Zap,
  BarChart3,
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  LayoutDashboard,
  CalendarRange,
  Users,
  Repeat2,
  FileText,
  ListChecks,
  Bell,
  Settings,
} from "lucide-react";

/**
 * Operational Workflow showcase — calm, premium hospitality dashboard.
 * Left: headline + 4 benefits. Right: realistic PMS calendar mock.
 */
export default function OperationalWorkflow() {
  const t = useT();
  return (
    <section
      className="relative overflow-hidden bg-white py-20 md:py-28"
      data-testid="operational-workflow-section"
    >
      <div
        className="pointer-events-none absolute -top-32 left-1/2 h-[420px] w-[820px] -translate-x-1/2 bg-[radial-gradient(closest-side,rgba(255,122,0,0.06),transparent)] blur-2xl"
        aria-hidden
      />

      <div className="container-akm relative">
        <div className="grid items-center gap-14 lg:grid-cols-[0.9fr_1.2fr] lg:gap-16">
          {/* ─── LEFT — Copy + benefits ─── */}
          <Reveal variant="left">
            <span className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#EA580C]">
              <Sparkles size={12} className="text-[#FF7A00]" />
              {t("Operacionet", "Operations")}
            </span>
            <h2
              className="mt-4 font-display font-extrabold tracking-[-0.035em] text-[#0F172A]"
              style={{
                fontSize: "clamp(1.85rem, 1.1rem + 1.7vw, 2.85rem)",
                lineHeight: 1.08,
              }}
              data-testid="operational-heading"
            >
              {t("Operacionet tuaja,", "Your operations,")}{" "}
              <span className="text-[#F26322]">{t("më të thjeshta se kurrë", "simpler than ever")}</span>
            </h2>
            <p className="mt-5 max-w-xl text-[15.5px] leading-[1.75] text-[#475569]">
              {t(
                "Akomoda organizon rezervimet, statusin e dhomave dhe operacionet e përditshme në një platformë të vetme.",
                "Akomoda organises reservations, room status and every daily operation on a single platform."
              )}
            </p>

            <ul className="mt-9 space-y-5">
              {[
                { icon: Eye,        title: t("Pamje e qartë e çdo dite","A clear view of every day"),                     desc: t("Shihni gjithë hotelin tuaj në një ekran.","See the whole hotel on one screen.") },
                { icon: BedDouble,  title: t("Status i dhomave në kohë reale","Real-time room status"),                   desc: t("Të zëna, të pastruara, të lira — gjithmonë të përditësuara.","Occupied, cleaned, free — always up to date.") },
                { icon: Zap,        title: t("Automatizime që ju kursejnë kohë","Automations that save you hours"),        desc: t("Lër sistemin të bëjë punët e përsëritura për ju.","Let the system handle the repetitive work for you.") },
                { icon: BarChart3,  title: t("Raporte të sakta për vendimmarrje më të mirë","Accurate reports for better decisions"), desc: t("Numrat që kanë rëndësi, gjithmonë në kohë.","The numbers that matter, always on time.") },
              ].map(({ icon: Icon, title, desc }, i) => (
                <li key={title} className="flex items-start gap-4">
                  <span
                    className="grid h-11 w-11 shrink-0 place-items-center rounded-xl border border-[#E2E8F0] bg-white shadow-[0_4px_14px_-6px_rgba(15,23,42,0.12)] ring-1 ring-black/[0.03]"
                    aria-hidden
                  >
                    <Icon size={18} className="text-[#0F172A]" strokeWidth={1.8} />
                  </span>
                  <div>
                    <div className="font-display text-[15px] font-bold tracking-tight text-[#0F172A]">
                      {title}
                    </div>
                    <div className="mt-1 text-[13.5px] leading-[1.55] text-[#64748B]">
                      {desc}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </Reveal>

          {/* ─── RIGHT — PMS Dashboard mockup ─── */}
          <Reveal variant="right" delay={150} duration={900}>
            <DashboardMock />
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────────────
   DASHBOARD MOCK — sidebar + topbar + calendar timeline
   ───────────────────────────────────────────────────────────────────── */
function DashboardMock() {
  const t = useT();
  return (
    <div
      className="relative mx-auto w-full overflow-hidden rounded-2xl bg-white shadow-[0_50px_120px_-40px_rgba(11,18,32,0.35),0_0_0_1px_rgba(15,23,42,0.05)] ring-1 ring-black/[0.04]"
      data-testid="operational-dashboard"
    >
      {/* Subtle orange glow behind */}
      <div
        className="pointer-events-none absolute -inset-6 -z-10 rounded-[40px] bg-[radial-gradient(closest-side,rgba(255,138,61,0.18),transparent_70%)] blur-2xl"
        aria-hidden
      />

      <div className="grid grid-cols-[160px_1fr]">
        {/* ─── Sidebar ─── */}
        <aside className="bg-[#0B1220] p-3 text-white">
          {/* Logo */}
          <div className="flex items-center gap-2 px-2 py-1.5">
            <span className="grid h-7 w-7 place-items-center rounded-lg bg-gradient-to-br from-[#FF9A3D] to-[#EA580C] font-display text-[11px] font-extrabold text-white shadow-[0_4px_12px_-2px_rgba(234,88,12,0.6)]">
              a
            </span>
            <span className="font-display text-[12.5px] font-extrabold tracking-tight">
              Akomoda
            </span>
          </div>

          {/* Nav */}
          <nav className="mt-5 space-y-0.5">
            {[
              { icon: LayoutDashboard, label: "Dashboard",                        active: true  },
              { icon: CalendarDays,    label: t("Rezervimet","Bookings")                     },
              { icon: CalendarRange,   label: t("Kalendari","Calendar")                      },
              { icon: BedDouble,       label: t("Dhomat","Rooms")                            },
              { icon: Users,           label: t("Klientët","Guests")                         },
              { icon: Repeat2,         label: "Channel Manager"                              },
              { icon: BarChart3,       label: t("Raporte","Reports")                         },
              { icon: FileText,        label: t("Faturat","Invoices")                        },
            ].map(({ icon: Icon, label, active }) => (
              <span
                key={label}
                className={`flex items-center gap-2 rounded-md px-2 py-1.5 text-[10.5px] font-medium ${
                  active
                    ? "bg-white/10 text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,0.06)]"
                    : "text-white/55 hover:text-white/85"
                }`}
              >
                <Icon size={12} strokeWidth={active ? 2.2 : 1.7} />
                {label}
              </span>
            ))}
          </nav>

          {/* Bottom small CTA */}
          <div className="mt-6 rounded-lg border border-white/[0.08] bg-white/[0.04] p-2.5">
            <div className="flex items-center gap-1.5">
              <Bell size={11} className="text-[#FF9A3D]" />
              <span className="text-[9.5px] font-semibold text-white">{t("3 njoftime","3 notifications")}</span>
            </div>
            <div className="mt-1 text-[8.5px] leading-tight text-white/45">
              {t("2 mbërritje sot · 1 fatura","2 arrivals today · 1 invoice")}
            </div>
          </div>
        </aside>

        {/* ─── Main content ─── */}
        <div className="bg-[#FAFBFC]">
          {/* Topbar */}
          <div className="flex items-center justify-between border-b border-[#E2E8F0] bg-white px-4 py-2.5">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1">
                <button type="button" className="grid h-6 w-6 place-items-center rounded border border-[#E2E8F0] bg-white text-[#64748B] hover:text-[#0F172A]">
                  <ChevronLeft size={12} />
                </button>
                <button type="button" className="grid h-6 w-6 place-items-center rounded border border-[#E2E8F0] bg-white text-[#64748B] hover:text-[#0F172A]">
                  <ChevronRight size={12} />
                </button>
              </div>
              <span className="font-display text-[12.5px] font-bold text-[#0F172A]">{t("Qershor 2026","June 2026")}</span>
              <span className="rounded-full bg-[#FFF1E5] px-2 py-0.5 text-[9.5px] font-semibold text-[#EA580C] ring-1 ring-[#FFD9B5]">
                {t("Sot · 10 Qer","Today · 10 Jun")}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search size={11} className="absolute left-2 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
                <input
                  readOnly
                  value={t("Kërko klient ose dhomë...","Search guest or room...")}
                  className="h-7 w-[180px] rounded-md border border-[#E2E8F0] bg-white pl-6 pr-2 text-[10px] text-[#64748B] outline-none"
                />
              </div>
              <button type="button" className="inline-flex items-center gap-1 rounded-md border border-[#E2E8F0] bg-white px-2 py-1 text-[10px] font-medium text-[#0F172A]">
                <Filter size={10} /> {t("Filtra","Filters")}
              </button>
              <button type="button" className="grid h-7 w-7 place-items-center rounded-md border border-[#E2E8F0] bg-white text-[#64748B]">
                <Settings size={11} />
              </button>
            </div>
          </div>

          {/* KPI strip */}
          <div className="grid grid-cols-4 gap-2 border-b border-[#E2E8F0] bg-white px-4 py-3">
            {[
              { k: t("Mbërritje","Arrivals"), v: "12", c: "text-[#0F172A]" },
              { k: t("Largime","Departures"), v: "8",  c: "text-[#0F172A]" },
              { k: t("Zënë","Occupancy"),     v: "84%", c: "text-emerald-600" },
              { k: "ADR",                     v: "€128", c: "text-[#EA580C]" },
            ].map((s) => (
              <div key={s.k} className="rounded-lg border border-[#F1F5F9] bg-[#FAFBFC] px-3 py-2">
                <div className="text-[9px] font-semibold uppercase tracking-[0.14em] text-[#94A3B8]">{s.k}</div>
                <div className={`mt-0.5 font-display text-[16px] font-extrabold leading-none ${s.c}`}>{s.v}</div>
              </div>
            ))}
          </div>

          {/* Calendar header (day labels) */}
          <div className="grid grid-cols-[88px_repeat(10,minmax(0,1fr))] gap-px bg-[#E2E8F0] text-center text-[9px] font-semibold text-[#64748B]">
            <div className="bg-white px-2 py-1.5 text-left">Dhoma</div>
            {[8, 9, 10, 11, 12, 13, 14, 15, 16, 17].map((d) => (
              <div
                key={d}
                className={`bg-white py-1.5 ${d === 10 ? "text-[#EA580C]" : ""}`}
              >
                {d}
              </div>
            ))}
          </div>

          {/* Calendar rows */}
          <div className="bg-[#E2E8F0]">
            {[
              { room: "101 · Std", bars: [
                { start: 0, span: 3, type: "reserved" },
                { start: 4, span: 2, type: "arrival" },
                { start: 7, span: 3, type: "checked-in" },
              ]},
              { room: "102 · Std", bars: [
                { start: 2, span: 4, type: "checked-in" },
                { start: 7, span: 2, type: "reserved" },
              ]},
              { room: "201 · Dlx", bars: [
                { start: 0, span: 2, type: "reserved" },
                { start: 3, span: 3, type: "arrival" },
                { start: 6, span: 4, type: "checked-in" },
              ]},
              { room: "202 · Dlx", bars: [
                { start: 0, span: 5, type: "blocked" },
                { start: 5, span: 5, type: "reserved" },
              ]},
              { room: "301 · Ste", bars: [
                { start: 1, span: 3, type: "checked-in" },
                { start: 5, span: 4, type: "reserved" },
              ]},
              { room: "302 · Ste", bars: [
                { start: 0, span: 2, type: "reserved" },
                { start: 3, span: 2, type: "arrival" },
                { start: 6, span: 3, type: "checked-in" },
              ]},
            ].map((r) => (
              <CalendarRow key={r.room} room={r.room} bars={r.bars} />
            ))}
          </div>

          {/* Footer legend */}
          <div className="flex flex-wrap items-center gap-3 border-t border-[#E2E8F0] bg-white px-4 py-2.5 text-[9.5px] text-[#64748B]">
            <Legend color="#22C55E" label="Rezervim" />
            <Legend color="#FACC15" label="Mbërritje" />
            <Legend color="#3B82F6" label="Check-in" />
            <Legend color="#EF4444" label="Bllokuar" />
            <span className="ml-auto inline-flex items-center gap-1 text-[#0F172A]">
              <ListChecks size={11} className="text-emerald-600" />
              <span className="font-semibold">8 pastruar</span> sot
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Calendar row ─── */
function CalendarRow({ room, bars }) {
  const BAR_STYLES = {
    reserved:    "bg-emerald-100 text-emerald-800 ring-emerald-200",
    arrival:     "bg-yellow-100 text-yellow-800 ring-yellow-200",
    "checked-in":"bg-sky-100 text-sky-800 ring-sky-200",
    blocked:     "bg-rose-100 text-rose-800 ring-rose-200",
  };
  const BAR_LABEL = {
    reserved: "Rezervuar",
    arrival: "Mbërritje",
    "checked-in": "Check-in",
    blocked: "Bllokuar",
  };
  return (
    <div className="grid grid-cols-[88px_repeat(10,minmax(0,1fr))] gap-px">
      <div className="flex items-center bg-white px-2 py-2 text-[10px] font-semibold text-[#0F172A]">
        {room}
      </div>
      {Array.from({ length: 10 }).map((_, i) => (
        <div key={i} className="relative bg-white">
          {bars.map((b, j) =>
            b.start === i ? (
              <div
                key={j}
                className={`absolute left-1 top-1.5 bottom-1.5 z-10 flex items-center rounded-md px-1.5 text-[9px] font-semibold ring-1 ${BAR_STYLES[b.type]}`}
                style={{ width: `calc(${b.span * 100}% - 6px)` }}
              >
                <span className="truncate">{BAR_LABEL[b.type]}</span>
              </div>
            ) : null,
          )}
          {/* Empty cell with subtle grid */}
          <div className="h-9" />
        </div>
      ))}
    </div>
  );
}

function Legend({ color, label }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="h-2 w-2 rounded-sm" style={{ background: color }} />
      <span>{label}</span>
    </span>
  );
}
